import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Settings, Mail, Save, Eye, EyeOff } from 'lucide-react';
import Swal from 'sweetalert2'; // <-- Import SweetAlert2
import 'sweetalert2/dist/sweetalert2.min.css'; // <-- Import the styles
import './EmailService.css';

const API_BASE = process.env.REACT_APP_API_URL;

const EmailService = ({ defaultTab = 'configuration' }) => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [emailConfig, setEmailConfig] = useState({
     host: 'smtp.gmail.com',
    EMAIL_USER: '',
    EMAIL_APP_PASSWORD: '',
    EMAIL_SERVICE: 'gmail',
    EMAIL_PORT: '587',
    EMAIL_SECURE: false
  });

  useEffect(() => {
    loadEmailConfig();
  }, []);

  const loadEmailConfig = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/email/settings`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` },
      });
      
      if (response.ok) {
        const config = await response.json();
         setEmailConfig(prev => ({
          ...prev,
          host: config.host || 'smtp.gmail.com',
          EMAIL_USER: config.user || '',
          EMAIL_PORT: config.port || '587',
          EMAIL_APP_PASSWORD: '',
          EMAIL_SECURE: config.port === 465,
        }));
      } else {
        console.error('Failed to load email config');
      }
    } catch (error) {
      console.error('Error loading email config:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveEmailConfig = async () => {
    try {
      setLoading(true);
      const payload = {
        host: emailConfig.host,
        port: emailConfig.EMAIL_PORT,
        user: emailConfig.EMAIL_USER,
        pass: emailConfig.EMAIL_APP_PASSWORD,
      };
      const response = await fetch(`${API_BASE}/api/email/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
        },
        body: JSON.stringify(payload),
      });
      
      const result = await response.json();
      if (response.ok) {
        // MODIFIED: Replaced alert with Swal
        Swal.fire({
          icon: 'success',
          title: 'Saved!',
          text: result.message || 'Email configuration saved successfully!',
        });
      } else {
        // MODIFIED: Replaced alert with Swal
        Swal.fire({
          icon: 'error',
          title: 'Save Failed',
          text: result.error || 'An unknown error occurred.',
        });
      }
    } catch (error) {
      console.error('Error saving email config:', error);
      // MODIFIED: Replaced alert with Swal
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to save email configuration. Please check the console.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = (field, value) => {
    setEmailConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const testEmailConnection = async () => {
    try {
      setLoading(true);
      const payload = {
        host: emailConfig.host,
        port: emailConfig.EMAIL_PORT,
        user: emailConfig.EMAIL_USER,
        pass: emailConfig.EMAIL_APP_PASSWORD,
      };
      const response = await fetch(`${API_BASE}/api/email/test-connection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
        },
        body: JSON.stringify(payload),
      });
      
      const result = await response.json();
      if (response.ok) {
        // MODIFIED: Replaced alert with Swal
        Swal.fire({
          icon: 'success',
          title: 'Connection Successful!',
          text: result.message || 'A test email was sent successfully. Please check the recipient\'s inbox.',
        });
      } else {
        // MODIFIED: Replaced alert with Swal
        Swal.fire({
          icon: 'error',
          title: 'Connection Test Failed',
          text: result.error || 'An unknown error occurred during the test.',
        });
      }
    } catch (error) {
      console.error('Error testing email config:', error);
      // MODIFIED: Replaced alert with Swal
      Swal.fire({
        icon: 'error',
        title: 'Test Error',
        text: 'Failed to test the email configuration. Check console for details.',
      });
    } finally {
      setLoading(false);
    }
  };

  // ... The rest of the component JSX remains unchanged ...
  return (
    <div className="email-service-page">
      <Helmet>
        <title>{loading ? 'Loading Email Config...' : 'Email Configuration | GN VISA Admin'}</title>
        <meta name="description" content="Configure SMTP settings for sending automated emails from the application." />
      </Helmet>
      <div className="email-service-header">
        <h1 className="email-service-title"><Settings size={24} /> Email Configuration</h1>
        <p className="email-service-description">Configure SMTP settings for sending emails from your application</p>
      </div>
      
      <div className="email-service-container">
        <div className="email-config-section">
          <div className="email-config-header">
            <h2 className="email-config-title">SMTP Configuration</h2>
            <p className="email-config-subtitle">Set up your email service provider settings to enable email functionality</p>
          </div>
          
          <div className="email-config-form">
            <div className="email-form-group">
              <label className="email-form-label">SMTP Host</label>
              <input type="text" placeholder="e.g., smtp.gmail.com" className="email-form-input" value={emailConfig.host} onChange={(e) => handleConfigChange('host', e.target.value)} disabled={loading} />
              <small className="email-field-help">The address of your email server.</small>
            </div>
            <div className="email-form-row">
              <div className="email-form-group">
                <label className="email-form-label">Email Service Provider</label>
                <select className="email-form-select" value={emailConfig.EMAIL_SERVICE} onChange={(e) => handleConfigChange('EMAIL_SERVICE', e.target.value)} disabled={loading}>
                  <option value="gmail">Gmail</option><option value="outlook">Outlook/Hotmail</option><option value="yahoo">Yahoo Mail</option><option value="custom">Custom SMTP</option>
                </select>
                <small className="email-field-help">Choose your email service provider or select custom for other SMTP servers</small>
              </div>
              <div className="email-form-group">
                <label className="email-form-label">Email Address</label>
                <input type="email" placeholder="your-email@example.com" className="email-form-input" value={emailConfig.EMAIL_USER} onChange={(e) => handleConfigChange('EMAIL_USER', e.target.value)} disabled={loading} />
                <small className="email-field-help">The email address that will be used to send emails</small>
              </div>
            </div>
            <div className="email-form-row">
              <div className="email-form-group">
                <label className="email-form-label">App Password</label>
                <div className="email-password-group">
                  <input type={showPassword ? "text" : "password"} placeholder="Enter app password" className="email-form-input" value={emailConfig.EMAIL_APP_PASSWORD} onChange={(e) => handleConfigChange('EMAIL_APP_PASSWORD', e.target.value)} disabled={loading} />
                  <button type="button" className="email-password-toggle" onClick={() => setShowPassword(!showPassword)}>{showPassword ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                </div>
                <small className="email-field-help">Use an app-specific password, not your regular email password. <a href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noopener noreferrer">Learn how to create app passwords</a></small>
              </div>
              <div className="email-form-group">
                <label className="email-form-label">SMTP Port</label>
                <input type="number" className="email-form-input" value={emailConfig.EMAIL_PORT} onChange={(e) => handleConfigChange('EMAIL_PORT', e.target.value)} disabled={loading} min="1" max="65535" />
                <small className="email-field-help">Common ports: 587 (TLS), 465 (SSL), 25 (Non-encrypted)</small>
              </div>
            </div>
            <div className="email-form-row">
              <div className="email-form-group email-checkbox-group">
                <input type="checkbox" id="secure-connection" checked={emailConfig.EMAIL_SECURE} onChange={(e) => handleConfigChange('EMAIL_SECURE', e.target.checked)} disabled={loading} />
                <label htmlFor="secure-connection" className="email-form-label">Use Secure Connection (TLS/SSL)</label>
                <small className="email-field-help">Enable for secure email transmission (recommended)</small>
              </div>
            </div>
          </div>
          <div className="email-config-actions">
            <button className="email-test-btn" onClick={testEmailConnection} disabled={loading || !emailConfig.EMAIL_USER || !emailConfig.EMAIL_APP_PASSWORD}><Mail size={16} />{loading ? 'Testing...' : 'Test Connection'}</button>
            <button className="email-save-btn" onClick={saveEmailConfig} disabled={loading || !emailConfig.EMAIL_USER || !emailConfig.EMAIL_APP_PASSWORD}><Save size={16} />{loading ? 'Saving...' : 'Save Configuration'}</button>
          </div>
        </div>
        <div className="email-config-status">
          <h3 className="email-status-title">Configuration Status</h3>
          <div className="email-status-grid">
            <div className="email-status-item"><div className="email-status-label">Email Service</div><div className={`email-status-value ${emailConfig.EMAIL_SERVICE ? 'configured' : 'not-configured'}`}>{emailConfig.EMAIL_SERVICE ? emailConfig.EMAIL_SERVICE.toUpperCase() : 'Not Set'}</div></div>
            <div className="email-status-item"><div className="email-status-label">Email Address</div><div className={`email-status-value ${emailConfig.EMAIL_USER ? 'configured' : 'not-configured'}`}>{emailConfig.EMAIL_USER || 'Not Set'}</div></div>
            <div className="email-status-item"><div className="email-status-label">App Password</div><div className={`email-status-value ${emailConfig.EMAIL_APP_PASSWORD ? 'configured' : 'not-configured'}`}>{emailConfig.EMAIL_APP_PASSWORD ? '••••••••' : 'Not Set'}</div></div>
            <div className="email-status-item"><div className="email-status-label">SMTP Port</div><div className={`email-status-value ${emailConfig.EMAIL_PORT ? 'configured' : 'not-configured'}`}>{emailConfig.EMAIL_PORT || 'Not Set'}</div></div>
            <div className="email-status-item"><div className="email-status-label">Secure Connection</div><div className={`email-status-value ${emailConfig.EMAIL_SECURE ? 'enabled' : 'disabled'}`}>{emailConfig.EMAIL_SECURE ? 'Enabled' : 'Disabled'}</div></div>
          </div>
        </div>
        <div className="email-config-help">
          <h3 className="email-help-title">Setup Help</h3>
          <div className="email-help-content">
            <div className="email-help-section"><h4>Gmail Setup:</h4><ul><li>Enable 2-factor authentication on your Google account</li><li>Generate an app-specific password</li><li>Use port 587 with TLS enabled</li></ul></div>
            <div className="email-help-section"><h4>Outlook Setup:</h4><ul><li>Use your full Outlook email address</li><li>Use your regular password or app password</li><li>Use port 587 with TLS enabled</li></ul></div>
            <div className="email-help-section"><h4>Security Notes:</h4><ul><li>Always use app-specific passwords when available</li><li>Enable secure connection (TLS/SSL) for data protection</li><li>Test your configuration before saving</li></ul></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailService;